import React, { useState, useEffect, useRef } from 'react';
import { db, auth } from '../utils/firebase-config';
import { collection, onSnapshot, doc, Timestamp } from 'firebase/firestore';
import { getUserDocs, importUserSnapshotDocument, ensureUsageInitialized, preflightSnapshotImport } from '../utils/userDataHelper';
import { BACKUP_COLLECTIONS, serializeBackupValue, deserializeBackupValue, validateBackup, planBackupImport } from '../utils/backupPlanning';
import Breadcrumbs from '../Components/Breadcrumbs';
import Footer from '../Components/Footer';
import './labcss/backup-settings.css';

/**
 * DataBackup - Export and import all lab data
 * Route: /lab/backup
 */

const LABELS = { ingredients: ['Ingredients', '📦'], recipes: ['Recipes', '📚'], batches: ['Batches', '📋'], sales: ['Sales', '💰'], notes: ['Notes', '📝'], purchases: ['Purchases', '🧾'], suppliers: ['Suppliers', '🏪'], packaging: ['Packaging', '🏷️'], reminders: ['Reminders', '⏰'], notifications: ['Notifications', '🔔'], settings: ['Settings', '⚙️'] };
const COLLECTIONS = BACKUP_COLLECTIONS.map(id => ({ id, label: LABELS[id][0], icon: LABELS[id][1] }));

function DataBackup() {
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importPreview, setImportPreview] = useState(null);
  const [message, setMessage] = useState({ type: '', text: '' });
  const fileInputRef = useRef(null);

  // Load all data from user-scoped collections
  useEffect(() => {
    const user = auth.currentUser;
    if (!user) { setLoading(false); return; }
    const uid = user.uid;
    const unsubs = [];
    const loadedData = {};
    const loaded = new Set();

    COLLECTIONS.forEach(col => {
      unsubs.push(onSnapshot(collection(db, 'users', uid, col.id), (snap) => {
        loadedData[col.id] = snap.docs.map(d => ({ ...d.data(), id: d.id }));
        loaded.add(col.id);
        setData({ ...loadedData });
        setLoading(loaded.size !== COLLECTIONS.length);
      }, error => { setMessage({ type: 'error', text: `Could not load ${col.label}: ${error.message}. Export will fetch and verify every collection again.` }); setLoading(false); }));
    });

    return () => unsubs.forEach(u => u());
  }, []);

  // Export all data as JSON
  const exportAllJSON = async () => {
    setExporting(true);
    try {
      const collections = Object.fromEntries(await Promise.all(COLLECTIONS.map(async col => [col.id, await getUserDocs(col.id)])));
      const exportData = {
        exportedAt: new Date().toISOString(),
        version: '2.0',
        backupId: `${auth.currentUser.uid}:${Date.now()}`,
        sourceUserId: auth.currentUser.uid,
        collections: serializeBackupValue(collections),
      };
      
      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `soap-lab-backup-${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      URL.revokeObjectURL(url);
      
      localStorage.setItem('lastBackupDate', new Date().toISOString());
      setMessage({ type: 'success', text: 'Complete backup exported successfully!' });
    } catch (error) {
      setMessage({ type: 'error', text: 'Export failed: ' + error.message });
    }
    setExporting(false);
  };

  // Export single collection as CSV
  const exportCSV = (collectionId) => {
    const items = data[collectionId] || [];
    if (items.length === 0) {
      setMessage({ type: 'error', text: 'No data to export' });
      return;
    }

    // Get all unique keys from all items
    const allKeys = new Set();
    items.forEach(item => {
      Object.keys(item).forEach(key => {
        if (typeof item[key] !== 'object' || item[key] === null) {
          allKeys.add(key);
        }
      });
    });
    const headers = Array.from(allKeys);

    // Build CSV
    const csvRows = [headers.join(',')];
    items.forEach(item => {
      const row = headers.map(h => {
        const val = item[h];
        if (val === null || val === undefined) return '';
        if (typeof val === 'string' && /[,"\r\n]/.test(val)) {
          return `"${val.replace(/"/g, '""')}"`;
        }
        return String(val);
      });
      csvRows.push(row.join(','));
    });

    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `soap-lab-${collectionId}-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    
    setMessage({ type: 'success', text: `${collectionId} exported to CSV!` });
  };

  // Handle file selection for import
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 30 * 1024 * 1024) { setMessage({ type: 'error', text: 'Backup files must be under 30 MB.' }); return; }

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const parsed = JSON.parse(event.target.result);
        
        validateBackup(parsed);

        // Count items
        const counts = {};
        COLLECTIONS.forEach(col => {
          counts[col.id] = (parsed.collections[col.id] || []).length;
        });

        setImportPreview({
          data: parsed,
          exportedAt: parsed.exportedAt,
          counts,
        });
      } catch (error) {
        setMessage({ type: 'error', text: 'Invalid file: ' + error.message });
      }
    };
    reader.readAsText(file);
  };

  // Import copies only; never delete current records or replay stock transactions.
  const performImport = async () => {
    if (!importPreview || !auth.currentUser) return;
    setImporting(true);
    let completed = 0;
    let plan = importPreview.plan;
    try {
      await ensureUsageInitialized();
      if (!plan) {
        const existing = Object.fromEntries(await Promise.all(COLLECTIONS.map(async col => [col.id, await getUserDocs(col.id)])));
        plan = planBackupImport(importPreview.data, existing, col => doc(collection(db, 'users', auth.currentUser.uid, col)).id);
        setImportPreview(current => ({ ...current, plan }));
      }
      await preflightSnapshotImport(plan.documents);
      for (const entry of plan.documents) {
        const restored = deserializeBackupValue(entry.data, (seconds, nanoseconds) => new Timestamp(seconds, nanoseconds));
        await importUserSnapshotDocument(entry.collection, entry.id, restored);
        completed += 1;
      }
      setMessage({ type: 'success', text: `Restored ${completed} documents${plan.remapped ? ' as linked copies with new IDs' : ' with their original IDs'}. Existing records were preserved.` });
      setImportPreview(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (error) {
      setMessage({ type: 'error', text: `Restore stopped after ${completed} of ${plan?.documents.length || 0} documents: ${error.message}. Existing records were not deleted. Keep this preview open and retry to resume safely.` });
    } finally { setImporting(false); }
  };

  // Calculate stats
  const totalItems = Object.values(data).reduce((sum, arr) => sum + (arr?.length || 0), 0);

  if (loading) {
    return (
      <>
        <div className="sl-page backup-page">
          <div className="pageheader borderleft">
            <Breadcrumbs items={[
              { label: 'Home', to: '/' },
              { label: 'Backup' }
            ]} />
            <div className="pageheading">Data Backup & Export</div>
          </div>
          <div className="backup-page-content borderleft">
            <section className="mypadding">
              <div className="bkp-loading">
                <div>Loading data...</div>
              </div>
            </section>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <div className="sl-page backup-page">
        <div className="pageheader borderleft">
          <Breadcrumbs items={[
            { label: 'Home', to: '/' },
            { label: 'Backup' }
          ]} />
          <div className="pageheading">Data Backup & Export</div>
          <div className="pagedescription">Export your data for backup or import from a previous backup</div>
        </div>

        <div className="backup-page-content borderleft">
          <section className="mypadding borderbottom">
            {/* Message */}
            {message.text && (
              <div className={`bkp-message ${message.type}`}>
                <span>{message.text}</span>
                <button className="bkp-message-close" onClick={() => setMessage({ type: '', text: '' })}>×</button>
              </div>
            )}

            {/* Stats Overview */}
            <div className="bkp-stats">
              <div className="bkp-stat-item">
                <div className="bkp-stat-label">Total Items</div>
                <div className="bkp-stat-value primary">{totalItems}</div>
              </div>
              {COLLECTIONS.map(col => (
                <div key={col.id} className="bkp-stat-item">
                  <div className="bkp-stat-label">{col.label}</div>
                  <div className="bkp-stat-value">{data[col.id]?.length || 0}</div>
                </div>
              ))}
            </div>

            {/* Export Section */}
            <div className="bkp-section">
              <h2 className="bkp-section-title">Export Data</h2>

              {/* Full Backup */}
              <div className="bkp-full-backup">
                <div>
                  <div className="bkp-full-backup-label">Complete Backup (JSON)</div>
                  <div className="bkp-full-backup-desc">Export all data in a single file for complete backup</div>
                </div>
                <button
                  className="bkp-btn-primary"
                  onClick={exportAllJSON}
                  disabled={exporting}
                >
                  {exporting ? 'Exporting...' : 'Download Backup'}
                </button>
              </div>

              {/* Individual CSV Exports */}
              <div className="bkp-csv-label">Export as CSV (spreadsheet)</div>
              <div className="bkp-csv-buttons">
                {COLLECTIONS.map(col => (
                  <button
                    key={col.id}
                    className="bkp-csv-btn"
                    onClick={() => exportCSV(col.id)}
                    disabled={(data[col.id]?.length || 0) === 0}
                  >
                    {col.label} ({data[col.id]?.length || 0})
                  </button>
                ))}
              </div>
            </div>

            {/* Import Section */}
            <div className="bkp-section">
              <h2 className="bkp-section-title">Import Data</h2>

              {!importPreview ? (
                <div className="bkp-upload-zone">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".json"
                    onChange={handleFileSelect}
                    style={{ display: 'none' }}
                    id="backup-file"
                  />
                  <label htmlFor="backup-file" className="bkp-upload-label">
                    Select Backup File
                  </label>
                  <div className="bkp-upload-hint">
                    Select a .json backup file to import
                  </div>
                </div>
              ) : (
                <div className="bkp-preview">
                  <h3 className="bkp-preview-title">Import Preview</h3>
                  <div className="bkp-preview-meta">
                    Backup from: {new Date(importPreview.exportedAt).toLocaleString()}
                  </div>

                  <div className="bkp-preview-grid">
                    {COLLECTIONS.map(col => (
                      <div key={col.id} className="bkp-preview-item">
                        <div className="bkp-preview-item-label">{col.label}</div>
                        <div className={`bkp-preview-item-count ${importPreview.counts[col.id] > 0 ? 'has-data' : 'empty'}`}>
                          {importPreview.counts[col.id]}
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="bkp-import-actions">
                    <button
                      className="bkp-btn-merge"
                      onClick={performImport}
                      disabled={importing}
                    >
                      {importing ? 'Importing...' : 'Import Linked Copies'}
                    </button>
                    <p className="bodytext">Existing records are preserved. Conflicting IDs are remapped together so recipes, stock movements, batches and sales stay linked. Restores run one atomic document transaction at a time; a failed restore can be resumed here.</p>
                    <button
                      className="bkp-btn-cancel"
                      disabled={importing}
                      onClick={() => {
                        setImportPreview(null);
                        if (fileInputRef.current) fileInputRef.current.value = '';
                      }}
                    >
                      Cancel
                    </button>
                  </div>

                  <div className="bkp-replace-warning">
                    <strong>Replace All</strong> will delete all existing data before importing!
                  </div>
                </div>
              )}
            </div>

            {/* Tips */}
            <div className="bkp-tips">
              <div className="bkp-tips-title">Tips</div>
              <ul>
                <li>Create regular backups to protect your data</li>
                <li>Use JSON backups for complete data preservation</li>
                <li>Use CSV exports for spreadsheet analysis</li>
                <li>Store backups in cloud storage (Google Drive, Dropbox) for extra safety</li>
              </ul>
            </div>
          </section>
        </div>
      <Footer />
      </div>
    </>
  );
}

export default DataBackup;
